import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  HardHat,
  DollarSign,
  Shield,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Clock,
  Smartphone,
  Star,
  Briefcase,
  Globe,
} from "lucide-react";
import type { Metadata } from "next";

export const metadata: Metadata = {
  title: "Find Construction Work in Australia | RateRight",
  description:
    "Earn $30-50/hour in construction. Sign up in 60 seconds, get matched to jobs near you. Free for workers — always. Perfect for backpackers and WHV holders.",
  openGraph: {
    title: "Find Construction Work in Australia | RateRight",
    description:
      "Earn $30-50/hour in construction. Sign up free, get matched instantly.",
    type: "website",
  },
};

export default function WorkerJoinPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Minimal header — no nav, pure conversion */}
      <header className="border-b">
        <div className="h-1 bg-brand-orange" />
        <div className="mx-auto flex h-12 max-w-screen-sm items-center justify-center px-4">
          <Image
            src="/logo_black.svg"
            alt="RateRight"
            width={120}
            height={34}
            className="h-6 w-auto"
            priority
          />
        </div>
      </header>

      {/* Hero — single focus */}
      <section className="px-4 pt-10 pb-8">
        <div className="mx-auto max-w-screen-sm text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <HardHat className="h-8 w-8 text-primary" />
          </div>

          <h1 className="mt-5 text-[1.75rem] font-extrabold leading-[1.15] tracking-tight sm:text-4xl">
            Earn $30–50/hr in construction.{" "}
            <span className="text-primary">Start tomorrow.</span>
          </h1>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Sign up in 60 seconds. Get matched to jobs near you.{" "}
            <strong className="text-foreground">Always free for workers.</strong>
          </p>

          <Link href="/worker/signup" className="mt-6 block">
            <Button className="h-14 w-full gap-3 text-base font-semibold shadow-lg shadow-primary/25 sm:w-80">
              Sign up free — 60 seconds
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>

          <p className="mt-3 text-sm text-muted-foreground">
            No fees. No catches. No experience required.
          </p>
        </div>
      </section>

      {/* Why RateRight */}
      <section className="border-t bg-muted/50 px-4 py-10">
        <div className="mx-auto max-w-screen-sm">
          <h2 className="text-center text-xl font-bold tracking-tight">
            Why workers choose RateRight
          </h2>

          <div className="mt-6 space-y-3">
            <BenefitCard
              icon={<DollarSign className="h-5 w-5 text-primary" />}
              title="$0 to sign up. $0 to get hired."
              description="We charge contractors, not you. It's free forever."
            />
            <BenefitCard
              icon={<Clock className="h-5 w-5 text-primary" />}
              title="60-second signup"
              description="Answer a few questions or just tell us about yourself by voice. Done."
            />
            <BenefitCard
              icon={<MapPin className="h-5 w-5 text-primary" />}
              title="Jobs near you"
              description="Get matched to work within kilometres of where you live. No long commutes."
            />
            <BenefitCard
              icon={<DollarSign className="h-5 w-5 text-primary" />}
              title="Know your pay upfront"
              description="See the hourly rate before you accept. No surprises, no guessing."
            />
            <BenefitCard
              icon={<Smartphone className="h-5 w-5 text-primary" />}
              title="100% mobile"
              description="Sign up, browse jobs, and get hired — all from your phone."
            />
            <BenefitCard
              icon={<Shield className="h-5 w-5 text-primary" />}
              title="Licensed contractors only"
              description="Every contractor is ABN-verified. Real companies, real jobs."
            />
          </div>
        </div>
      </section>

      {/* For backpackers / WHV */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-screen-sm">
          <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-6">
            <div className="flex items-center gap-3">
              <Globe className="h-6 w-6 text-primary" />
              <h2 className="text-lg font-bold">On a Working Holiday Visa?</h2>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Construction is one of the best-paid industries for WHV holders in
              Australia. No local experience required — we match you with
              contractors who provide training and support.
            </p>
            <ul className="mt-4 space-y-2">
              <BenefitCheck text="International phone numbers accepted" />
              <BenefitCheck text="No ABN needed to sign up" />
              <BenefitCheck text="Get your White Card with on-the-job training" />
              <BenefitCheck text="Earn regional days for visa extension" />
            </ul>
          </div>
        </div>
      </section>

      {/* How it works — 3 steps */}
      <section className="border-t bg-muted/50 px-4 py-10">
        <div className="mx-auto max-w-screen-sm">
          <h2 className="text-center text-xl font-bold tracking-tight">
            How it works
          </h2>

          <div className="mt-6 space-y-4">
            <StepCard
              number="1"
              title="Sign up in 60 seconds"
              description="Your name, trade, location. That's it. Or just record a voice message and we'll fill it in for you."
            />
            <StepCard
              number="2"
              title="Get matched to jobs"
              description="Our AI matches you to jobs near you based on your skills and experience. You'll get notified instantly."
            />
            <StepCard
              number="3"
              title="Start work"
              description="Accept a match, show up on site. Get paid directly by the contractor — we never touch your money."
            />
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-screen-sm text-center">
          <div className="flex items-center justify-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className="h-5 w-5 fill-yellow-400 text-yellow-400"
              />
            ))}
          </div>
          <p className="mt-3 text-sm italic text-muted-foreground">
            &ldquo;I signed up on my phone during lunch. Had a job offer by
            dinner. Construction work 3km from my hostel.&rdquo;
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            — Backpacker worker, Sydney
          </p>

          <div className="mt-8 flex items-center justify-center gap-8">
            <Stat value="300+" label="Contractors" />
            <Stat value="$0" label="Worker fees" />
            <Stat value="60s" label="To sign up" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary px-4 py-12 text-primary-foreground">
        <div className="mx-auto max-w-screen-sm text-center">
          <h2 className="text-2xl font-bold">Ready to earn?</h2>
          <p className="mt-2 text-primary-foreground/80">
            Join workers across Australia finding construction jobs with
            RateRight.
          </p>
          <Link href="/worker/signup" className="mt-6 block">
            <Button
              variant="secondary"
              className="h-14 w-full gap-3 text-base font-semibold sm:w-80"
            >
              <HardHat className="h-5 w-5" />
              Sign up free — 60 seconds
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
          <p className="mt-3 text-sm text-primary-foreground/60">
            Already have an account?{" "}
            <Link
              href="/auth/login"
              className="underline hover:no-underline text-primary-foreground/80"
            >
              Sign in
            </Link>
          </p>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="border-t px-4 py-6">
        <div className="mx-auto max-w-screen-sm text-center">
          <p className="text-xs text-muted-foreground">
            &copy; 2026 RateRight Pty Ltd · ABN 88 688 279 769
          </p>
          <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
            <Link
              href="/legal/terms-of-service"
              className="hover:text-foreground min-h-[44px] flex items-center"
            >
              Terms
            </Link>
            <Link
              href="/legal/privacy-policy"
              className="hover:text-foreground min-h-[44px] flex items-center"
            >
              Privacy
            </Link>
            <Link
              href="/faq"
              className="hover:text-foreground min-h-[44px] flex items-center"
            >
              FAQ
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BenefitCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="flex gap-4 p-4">
        <div className="mt-0.5 shrink-0">{icon}</div>
        <div>
          <h3 className="font-semibold text-sm">{title}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StepCard({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="flex gap-4 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground">
          {number}
        </div>
        <div className="pt-0.5">
          <h3 className="font-semibold">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function BenefitCheck({ text }: { text: string }) {
  return (
    <li className="flex items-center gap-2">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
      <span className="text-sm">{text}</span>
    </li>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-2xl font-extrabold text-primary">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
